import type { TableColumnsType } from 'antd-v5';
import type {
  ColumnFilterItem,
  SorterResult,
} from 'antd-v5/es/table/interface';

// antd only draws the sort and filter controls; the rows are sorted, filtered,
// grouped and paged here

// --- Query state -------------------------------------------------------------

export type SortOrder = 'ascend' | 'descend';

export interface SortState {
  columnKey: string;
  order: SortOrder;
}

export type FilterKey = string | number | boolean;

/** By column key; `null` or missing when inactive */
export type FilterState = Record<string, FilterKey[] | null>;

export interface TableQueryState {
  sort: SortState | null;
  /** Active filters only */
  filters: FilterState;
  /** The columns' `defaultFilteredValue`s */
  defaultFilters: FilterState;
  /** 1-based; clamped to the last page while deriving */
  page: number;
  pageSize: number;
  collapsed: string[];
}

export type TableQueryAction =
  | { type: 'tableChange'; sort: SortState | null; filters: FilterState }
  | { type: 'paginate'; page: number; pageSize: number }
  | { type: 'resetPage' }
  /** `page`: where the group's header lands after the toggle */
  | { type: 'toggleGroup'; key: string; page?: number }
  | { type: 'defaultFilters'; defaultFilters: FilterState };

export const DEFAULT_PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function activeValues(
  values: readonly FilterKey[] | null | undefined,
): readonly FilterKey[] | null {
  return values?.length ? values : null;
}

function sameValues(
  a: readonly FilterKey[] | null | undefined,
  b: readonly FilterKey[] | null | undefined,
): boolean {
  const left = activeValues(a);
  const right = activeValues(b);
  if (!left || !right) return left === right;
  return left.length === right.length && left.every(v => right.includes(v));
}

/** Order-insensitive; an inactive column matches a missing one */
export function sameFilters(a: FilterState, b: FilterState): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return Array.from(keys).every(key => sameValues(a[key], b[key]));
}

function sameSort(a: SortState | null, b: SortState | null): boolean {
  return a?.columnKey === b?.columnKey && a?.order === b?.order;
}

function activeFilters(filters: FilterState): FilterState {
  return Object.fromEntries(
    Object.entries(filters).filter(([, values]) => !!values?.length),
  );
}

/**
 * Filter and page-size changes reset the page; a sort change keeps it.
 * New defaults replace only the filters still at their old default.
 */
export function tableQueryReducer(
  state: TableQueryState,
  action: TableQueryAction,
): TableQueryState {
  switch (action.type) {
    case 'tableChange': {
      const filtersChanged = !sameFilters(state.filters, action.filters);
      const sortChanged = !sameSort(state.sort, action.sort);
      if (!filtersChanged && !sortChanged) return state;
      return {
        ...state,
        sort: sortChanged ? action.sort : state.sort,
        filters: filtersChanged ? activeFilters(action.filters) : state.filters,
        page: filtersChanged ? 1 : state.page,
      };
    }
    case 'paginate': {
      const page = action.pageSize === state.pageSize ? action.page : 1;
      if (page === state.page && action.pageSize === state.pageSize)
        return state;
      return { ...state, page, pageSize: action.pageSize };
    }
    case 'resetPage':
      return state.page === 1 ? state : { ...state, page: 1 };
    case 'toggleGroup':
      return {
        ...state,
        collapsed: state.collapsed.includes(action.key)
          ? state.collapsed.filter(key => key !== action.key)
          : [...state.collapsed, action.key],
        page: action.page ?? state.page,
      };
    case 'defaultFilters': {
      const { defaultFilters } = action;
      if (sameFilters(state.defaultFilters, defaultFilters)) return state;
      const filters = { ...state.filters };
      const keys = new Set([
        ...Object.keys(state.defaultFilters),
        ...Object.keys(defaultFilters),
      ]);
      keys.forEach(key => {
        if (sameValues(state.filters[key], state.defaultFilters[key]))
          filters[key] = defaultFilters[key] ?? null;
      });
      return { ...state, filters: activeFilters(filters), defaultFilters };
    }
    default:
      return state;
  }
}

// --- Columns -----------------------------------------------------------------

type Column<T> = TableColumnsType<T>[number];

/** `order` is informative only, as in antd */
export type RowCompare<T> = (a: T, b: T, order: SortOrder) => number;
export type RowPredicate<T> = (value: FilterKey, record: T) => boolean;

export interface QueryFns<T> {
  compare: Record<string, RowCompare<T>>;
  predicates: Record<string, RowPredicate<T>>;
}

/** Same as antd's `getColumnKey` */
export function columnKeyOf<T>(column: Column<T>): string | undefined {
  if (column.key != null) return String(column.key);
  const dataIndex = 'dataIndex' in column ? column.dataIndex : undefined;
  if (Array.isArray(dataIndex)) return dataIndex.join('.');
  return dataIndex != null && dataIndex !== '' ? String(dataIndex) : undefined;
}

/** Sort and filters from the visible columns' defaults, page 1 */
export function initialTableQuery<T>(
  columns: TableColumnsType<T>,
  opts: { pageSize?: number } = {},
): TableQueryState {
  const sortColumn = columns.find(
    column =>
      !column.hidden &&
      !!column.sorter &&
      !!column.defaultSortOrder &&
      columnKeyOf(column) !== undefined,
  );
  const key = sortColumn && columnKeyOf(sortColumn);
  const order = sortColumn?.defaultSortOrder;
  const defaultFilters: FilterState = {};
  columns.forEach(column => {
    const filterKey = columnKeyOf(column);
    const values = column.defaultFilteredValue;
    if (filterKey !== undefined && !column.hidden && values?.length) {
      defaultFilters[filterKey] = [...values] as FilterKey[];
    }
  });
  return {
    sort: key !== undefined && order ? { columnKey: key, order } : null,
    filters: defaultFilters,
    defaultFilters,
    page: 1,
    pageSize: opts.pageSize ?? DEFAULT_PAGE_SIZE,
    collapsed: [],
  };
}

/** From `onChange`'s `sorter`; a cleared sort has no `order`. */
export function toSortState<T>(
  sorter: SorterResult<T> | SorterResult<T>[],
): SortState | null {
  const active = (Array.isArray(sorter) ? sorter : [sorter]).find(
    result => !!result.order,
  );
  const order = active?.order;
  if (!active || !order) return null;
  const key = active.columnKey ?? (active.column && columnKeyOf(active.column));
  return key == null ? null : { columnKey: String(key), order };
}

function sortFunctionOf<T>(
  sorter: Column<T>['sorter'],
): RowCompare<T> | undefined {
  if (typeof sorter === 'function') return sorter;
  if (sorter && typeof sorter === 'object' && sorter.compare)
    return sorter.compare;
  return undefined;
}

/** By column key; hidden columns and column groups are left out */
export function extractQueryFns<T>(columns: TableColumnsType<T>): QueryFns<T> {
  const compare: Record<string, RowCompare<T>> = {};
  const predicates: Record<string, RowPredicate<T>> = {};
  columns.forEach(column => {
    const key = columnKeyOf(column);
    if (key === undefined || column.hidden) return;
    const sortFn = sortFunctionOf<T>(column.sorter);
    if (sortFn) compare[key] = sortFn;
    if (column.onFilter) predicates[key] = column.onFilter;
  });
  return { compare, predicates };
}

/**
 * The columns in antd's "remote" mode: antd draws the sort/filter UI and calls
 * `onChange` without touching the data. `defaultFilteredValue` stays for Reset.
 */
export function controlColumns<T>(
  columns: TableColumnsType<T>,
  state: Pick<TableQueryState, 'sort' | 'filters'>,
  opts: { disableSort?: readonly string[] } = {},
): TableColumnsType<T> {
  return columns.map(column => {
    const key = columnKeyOf(column);
    const controlled = { ...column };
    // Deleted, not set to `undefined`: antd checks `'onFilter' in column`
    delete controlled.onFilter;
    delete controlled.defaultSortOrder;
    if (column.sorter) {
      if (key !== undefined && opts.disableSort?.includes(key)) {
        delete controlled.sorter;
        delete controlled.sortOrder;
      } else {
        controlled.sorter = true;
        controlled.sortOrder =
          key !== undefined && state.sort?.columnKey === key
            ? state.sort.order
            : null;
      }
    }
    if (column.filters || 'filterDropdown' in column) {
      controlled.filteredValue =
        (key !== undefined && state.filters[key]) || null;
    }
    return controlled;
  });
}

function filterValuesOf(
  items: readonly ColumnFilterItem[],
  values = new Set<string>(),
): Set<string> {
  items.forEach(item => {
    values.add(String(item.value));
    if (item.children) filterValuesOf(item.children, values);
  });
  return values;
}

/**
 * Drops filter values that lost their option (e.g. a deleted group), which
 * antd keeps selected. `filterDropdown` columns keep theirs.
 */
export function pruneFilters<T>(
  filters: FilterState,
  columns: TableColumnsType<T>,
): FilterState {
  const options = new Map<string, Set<string>>();
  columns.forEach(column => {
    const key = columnKeyOf(column);
    if (key !== undefined && column.filters && !column.filterDropdown)
      options.set(key, filterValuesOf(column.filters));
  });
  const hasOption = (key: string, value: FilterKey) =>
    options.get(key)?.has(String(value)) ?? true;
  const stale = Object.entries(filters).some(([key, values]) =>
    values?.some(value => !hasOption(key, value)),
  );
  if (!stale) return filters;
  return activeFilters(
    Object.fromEntries(
      Object.entries(filters).map(([key, values]) => [
        key,
        values?.filter(value => hasOption(key, value)) ?? null,
      ]),
    ),
  );
}

// --- Filter and sort ---------------------------------------------------------

/** A row passes a column when any selected value matches, as in antd. */
export function applyFilters<T>(
  rows: readonly T[],
  predicates: Record<string, RowPredicate<T>>,
  filters: FilterState,
  opts: { skip?: readonly string[] } = {},
): T[] {
  const active = Object.entries(filters).flatMap(([key, values]) => {
    const predicate = predicates[key];
    if (!values?.length || !predicate || opts.skip?.includes(key)) return [];
    return [{ predicate, values }];
  });
  if (!active.length) return rows.slice();
  return rows.filter(row =>
    active.every(({ predicate, values }) =>
      values.some(value => predicate(value, row)),
    ),
  );
}

/** Stable; the comparator result is negated for `descend`, as in antd. */
export function applySort<T>(
  rows: readonly T[],
  compare: Record<string, RowCompare<T>>,
  sort: SortState | null,
): T[] {
  const compareFn = sort ? compare[sort.columnKey] : undefined;
  if (!sort || !compareFn) return rows.slice();
  const sign = sort.order === 'descend' ? -1 : 1;
  // Array#sort is stable (ES2019)
  return rows.slice().sort((a, b) => sign * compareFn(a, b, sort.order));
}

/** Can hide rows its default shows. A filter wider than its default can't. */
export function narrows(
  values: readonly FilterKey[] | null | undefined,
  defaultValues: readonly FilterKey[] | null | undefined,
): boolean {
  const current = activeValues(values);
  if (!current) return false;
  const defaults = activeValues(defaultValues);
  return !defaults || defaults.some(value => !current.includes(value));
}

/** A search or a narrowing filter may hide rows the defaults show. */
export function hasActiveQuery(
  state: { search?: string } & Pick<TableQueryState, 'filters'> &
    Partial<Pick<TableQueryState, 'defaultFilters'>>,
  opts: { skip?: readonly string[] } = {},
): boolean {
  if (state.search?.trim()) return true;
  const { defaultFilters = {} } = state;
  return Object.entries(state.filters).some(
    ([key, values]) =>
      !opts.skip?.includes(key) && narrows(values, defaultFilters[key]),
  );
}

// --- Sections ----------------------------------------------------------------

export interface GroupDef {
  key: string;
}

// Symbols, so no field of the caller's rows can collide with them
const GROUP_ROW = Symbol('groupRow');
const PLACEMENT = Symbol('placement');

/** Never give it a `children` key: antd would switch to tree mode */
export interface GroupHeaderRow<TGroup> {
  readonly [GROUP_ROW]: true;
  /** `group:<groupKey>` */
  rowKey: string;
  groupKey: string;
  /** Unset for a fallback group missing from `groups` */
  group?: TGroup;
  /** Rows of the group, collapsed or not */
  count: number;
  collapsed: boolean;
  /** The group started on an earlier page */
  continued: boolean;
}

interface Placement {
  /** `<groupKey>:<rowId>` */
  rowKey: string;
  groupKey: string;
  rowId: string;
}

/** A copy of a row per group it is in, so each has its own key */
export type GroupedItem<TData> = TData & { readonly [PLACEMENT]: Placement };

export type TableRow<TData, TGroup> = TData | GroupHeaderRow<TGroup>;

export function isGroupHeaderRow<TGroup>(
  row: unknown,
): row is GroupHeaderRow<TGroup> {
  return typeof row === 'object' && row !== null && GROUP_ROW in row;
}

function placementOf(row: object): Placement | undefined {
  return (row as Partial<GroupedItem<object>>)[PLACEMENT];
}

/** Group of a grouped row, for cells that depend on it */
export function groupKeyOf(row: object): string | undefined {
  return placementOf(row)?.groupKey;
}

/** The row's own key, or `<groupKey>:<rowId>` for a grouped copy */
export function rowKeyOf<TData extends object, TGroup>(
  row: TableRow<TData, TGroup>,
  getRowId: (row: TData) => string,
): string {
  if (isGroupHeaderRow(row)) return row.rowKey;
  return placementOf(row)?.rowKey ?? getRowId(row as TData);
}

export interface Section<TData, TGroup> {
  key: string;
  group?: TGroup;
  rows: GroupedItem<TData>[];
}

export interface SectionsOptions<TData, TGroup extends GroupDef> {
  groups: readonly TGroup[];
  getGroupKeys: (row: TData) => readonly string[];
  fallbackKey: string;
  getRowId: (row: TData) => string;
}

/**
 * One section per group, in order, the fallback last; rows in input order. A
 * row is in each of its listed groups, or in the fallback one.
 */
export function buildSections<TData extends object, TGroup extends GroupDef>(
  rows: readonly TData[],
  opts: SectionsOptions<TData, TGroup>,
): Section<TData, TGroup>[] {
  const { groups, getGroupKeys, fallbackKey, getRowId } = opts;
  const keys = [
    ...groups.map(group => group.key).filter(key => key !== fallbackKey),
    fallbackKey,
  ];
  const groupsByKey = new Map(groups.map(group => [group.key, group]));
  const rowsByKey = new Map<string, GroupedItem<TData>[]>(
    keys.map(key => [key, []]),
  );
  rows.forEach(row => {
    const rowId = getRowId(row);
    const own = Array.from(new Set(getGroupKeys(row))).filter(key =>
      rowsByKey.has(key),
    );
    (own.length ? own : [fallbackKey]).forEach(groupKey =>
      rowsByKey.get(groupKey)?.push({
        ...row,
        [PLACEMENT]: { rowKey: `${groupKey}:${rowId}`, groupKey, rowId },
      }),
    );
  });
  return keys.map(key => ({
    key,
    group: groupsByKey.get(key),
    rows: rowsByKey.get(key) ?? [],
  }));
}

interface FilterSectionsOptions {
  /** Group keys to keep; all when empty or unset */
  selectedKeys?: readonly (string | number | boolean)[] | null;
  /** Keep the sections without rows (never the fallback one) */
  showEmpty: boolean;
  fallbackKey: string;
}

export function filterSections<TData, TGroup>(
  sections: readonly Section<TData, TGroup>[],
  opts: FilterSectionsOptions,
): Section<TData, TGroup>[] {
  const selected = opts.selectedKeys?.length
    ? new Set(opts.selectedKeys.map(String))
    : undefined;
  return sections.filter(({ key, rows }) => {
    if (selected && !selected.has(key)) return false;
    if (rows.length) return true;
    return opts.showEmpty && key !== opts.fallbackKey;
  });
}

// --- Paging ------------------------------------------------------------------

/** `whole-groups`: a page holds `pageSize` groups. `people`: `pageSize` rows. */
export type GroupPaging = 'whole-groups' | 'people';

export interface TablePage<R> {
  rows: R[];
  /** Clamped to [1, last page] */
  page: number;
  /** What the pager counts: rows, or groups with `whole-groups` */
  total: number;
}

export interface PaginateOptions {
  page: number;
  pageSize: number;
  collapsed: ReadonlySet<string>;
}

function lastPageOf(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

function clampPage(page: number, total: number, pageSize: number): number {
  return Math.min(
    Math.max(1, Math.floor(page) || 1),
    lastPageOf(total, pageSize),
  );
}

export function paginateFlat<R>(
  rows: readonly R[],
  page: number,
  pageSize: number,
): TablePage<R> {
  const size = Math.max(1, pageSize);
  const total = rows.length;
  const current = clampPage(page, total, size);
  const start = (current - 1) * size;
  return { rows: rows.slice(start, start + size), page: current, total };
}

function headerOf<TData, TGroup>(
  section: Section<TData, TGroup>,
  collapsed: boolean,
  continued: boolean,
): GroupHeaderRow<TGroup> {
  return {
    [GROUP_ROW]: true,
    rowKey: `group:${section.key}`,
    groupKey: section.key,
    group: section.group,
    count: section.rows.length,
    collapsed,
    continued,
  };
}

function visibleRowCount<TData, TGroup>(
  sections: readonly Section<TData, TGroup>[],
  collapsed: ReadonlySet<string>,
): number {
  return sections.reduce(
    (sum, { key, rows }) => sum + (collapsed.has(key) ? 0 : rows.length),
    0,
  );
}

function headerPage(offset: number, pageSize: number, lastPage: number) {
  return Math.min(Math.floor(offset / pageSize) + 1, lastPage);
}

/**
 * `pageSize` rows, with a header before each section's first row on the page.
 * An empty or collapsed section's header shows alone, at its place.
 */
export function paginatePeople<TData, TGroup>(
  sections: readonly Section<TData, TGroup>[],
  opts: PaginateOptions,
): TablePage<TableRow<GroupedItem<TData>, TGroup>> {
  const { collapsed } = opts;
  const pageSize = Math.max(1, opts.pageSize);
  const total = visibleRowCount(sections, collapsed);
  const page = clampPage(opts.page, total, pageSize);
  const lastPage = lastPageOf(total, pageSize);
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  const pageRows: TableRow<GroupedItem<TData>, TGroup>[] = [];
  let offset = 0;
  sections.forEach(section => {
    const { key, rows } = section;
    const isCollapsed = collapsed.has(key);
    if (isCollapsed || !rows.length) {
      if (headerPage(offset, pageSize, lastPage) === page)
        pageRows.push(headerOf(section, isCollapsed, false));
      return;
    }
    const from = Math.max(start, offset);
    const to = Math.min(end, offset + rows.length);
    if (from < to) {
      pageRows.push(
        headerOf(section, false, from > offset),
        ...rows.slice(from - offset, to - offset),
      );
    }
    offset += rows.length;
  });
  return { rows: pageRows, page, total };
}

/** `pageSize` sections a page, each with all its rows */
export function paginateWholeGroups<TData, TGroup>(
  sections: readonly Section<TData, TGroup>[],
  opts: PaginateOptions,
): TablePage<TableRow<GroupedItem<TData>, TGroup>> {
  const { page, rows } = paginateFlat(sections, opts.page, opts.pageSize);
  return {
    rows: rows.flatMap(section => {
      const isCollapsed = opts.collapsed.has(section.key);
      const header = headerOf(section, isCollapsed, false);
      return isCollapsed ? [header] : [header, ...section.rows];
    }),
    page,
    total: sections.length,
  };
}

/** Page of a group's first header with `people` paging */
export function pageOfGroup<TData, TGroup>(
  sections: readonly Section<TData, TGroup>[],
  key: string,
  opts: Omit<PaginateOptions, 'page'>,
): number | undefined {
  const { collapsed } = opts;
  const pageSize = Math.max(1, opts.pageSize);
  const lastPage = lastPageOf(visibleRowCount(sections, collapsed), pageSize);
  let offset = 0;
  for (const section of sections) {
    if (section.key === key) return headerPage(offset, pageSize, lastPage);
    if (!collapsed.has(section.key)) offset += section.rows.length;
  }
  return undefined;
}

// --- Summary -----------------------------------------------------------------

export interface GroupedSummary {
  /** Sections other than the fallback one */
  groups: number;
  /** Unique rows across the sections */
  items: number;
  /** Rows, collapsed ones included */
  rows: number;
}

export function summarize<TData, TGroup>(
  sections: readonly Section<TData, TGroup>[],
  fallbackKey: string,
): GroupedSummary {
  const items = new Set<string>();
  let groups = 0;
  let rows = 0;
  sections.forEach(section => {
    if (section.key !== fallbackKey) groups += 1;
    rows += section.rows.length;
    section.rows.forEach(row => items.add(row[PLACEMENT].rowId));
  });
  return { groups, items: items.size, rows };
}
